import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface BetaFeatureToastProps {
  title: string;
  description: string;
  buttonText: string;
  targetRoute: string;
  onShow?: () => void;
  onClose?: () => void;
  onNavigate?: () => void;
}

// Global flag to track toast visibility across renders
let globalToastId: string | null = null;

const BetaFeatureToast: React.FC<BetaFeatureToastProps> = ({
  title,
  description,
  buttonText,
  targetRoute,
  onShow,
  onClose,
  onNavigate
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Dismiss any existing toast immediately
    if (globalToastId) {
      toast.remove(globalToastId);
      globalToastId = null;
    }
    
    // Call onShow callback
    onShow?.();
    
    // Small delay to ensure previous toast is fully dismissed
    const timeoutId = setTimeout(() => {
      globalToastId = toast.custom(
        (t) => (
          <div
            className={`${
              t.visible ? 'animate-enter' : 'animate-leave'
            } relative max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
            onAnimationEnd={(e) => {
              // If the leave animation just finished, remove the toast
              if (e.animationName === 'leave' && !t.visible) {
                toast.remove(t.id);
              }
            }}
          >
            {/* Close button */}
            <button
              onClick={() => {
                toast.dismiss(t.id);
                onClose?.();
              }}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex-1 w-0 p-4 pr-8">
              <div className="flex items-start">
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {title}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {description}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex border-l border-gray-200">
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  onNavigate?.();
                  navigate(targetRoute);
                }}
                className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {buttonText}
              </button>
            </div>
          </div>
        ),
        {
          duration: Infinity,
          position: 'bottom-right',
        }
      );
    }, 50);

    // Cleanup function to dismiss toast on unmount
    return () => {
      clearTimeout(timeoutId);
      if (globalToastId) {
        toast.remove(globalToastId);
        globalToastId = null;
      }
    };
  }, [navigate, title, description, buttonText, targetRoute, onShow, onClose, onNavigate]);

  // This component doesn't render anything visible itself
  return null;
};

export default BetaFeatureToast;